import { useState } from 'react';
import { Link } from 'react-router-dom';
import HERO_IMAGES from '../heroImages';
import SEO from '../components/SEO/SEO';
import {
  Briefcase, Upload, CheckCircle2, ArrowRight, Code2, Shield,
  TrendingUp, AlertCircle, X, FileText,
} from 'lucide-react';
import './CareersPage.css';

const openings = [
  { role: 'Full Stack Developer', type: 'Full-time', dept: 'Engineering', skills: ['React', 'Node.js', 'PostgreSQL'] },
  { role: 'UI/UX Designer', type: 'Full-time', dept: 'Design', skills: ['Figma', 'Prototyping', 'Design Systems'] },
  { role: 'Cybersecurity Analyst', type: 'Full-time', dept: 'Security', skills: ['Penetration Testing', 'OWASP', 'SIEM'] },
  { role: 'DevOps Engineer', type: 'Full-time', dept: 'Infrastructure', skills: ['AWS', 'Docker', 'CI/CD'] },
  { role: 'Business Development Executive', type: 'Full-time', dept: 'Sales', skills: ['Client Relations', 'Proposals', 'CRM'] },
];

const perks = [
  { icon: <TrendingUp size={22} />, title: 'Startup Pace', desc: 'Work on real products that ship fast with no bureaucracy slowing you down.' },
  { icon: <Shield size={22} />, title: 'Learn Security', desc: 'Get hands-on with cybersecurity practices embedded in real-world projects.' },
  { icon: <Code2 size={22} />, title: 'Ownership', desc: 'Own your work end-to-end. Your contributions directly impact the product and clients.' },
  { icon: <Briefcase size={22} />, title: 'Growth Path', desc: 'Clear career progression with mentorship from senior engineers and designers.' },
];

const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB

// Helper: convert file to base64
const fileToBase64 = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result);
    reader.onerror = (err) => reject(err);
  });

const formatFileSize = (bytes) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
};

export default function CareersPage() {
  const [form, setForm] = useState({
    name: '',
    education: '',
    role: '',
    email: '',
    phone: '',
    coverLetter: '',
  });
  const [resumeFile, setResumeFile] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // ─── OTP verification state ───
  const [otpToken, setOtpToken] = useState('');
  const [otpCode, setOtpCode] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [otpVerified, setOtpVerified] = useState(false);
  const [otpSending, setOtpSending] = useState(false);
  const [otpError, setOtpError] = useState('');
  const [otpVerifiedEmail, setOtpVerifiedEmail] = useState('');

  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  const handleSendOtp = async () => {
    setOtpError('');
    if (!emailRegex.test(form.email)) {
      setOtpError('Enter a valid email address first.');
      return;
    }
    setOtpSending(true);
    try {
      const res = await fetch('/api/send-otp', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: form.email, purpose: 'careers' }),
      });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.error || 'Failed to send verification code.');
      }
      setOtpToken(data.otpToken);
      setOtpSent(true);
      setOtpVerified(false);
      setOtpCode('');
    } catch (err) {
      setOtpError(err.message || 'Something went wrong sending the code.');
    } finally {
      setOtpSending(false);
    }
  };

  const handleVerifyOtp = () => {
    setOtpError('');
    if (!otpCode || otpCode.length !== 6) {
      setOtpError('Enter the 6-digit code from your email.');
      return;
    }
    setOtpVerified(true);
    setOtpVerifiedEmail(form.email);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    if (error) setError('');
    if (name === 'email') {
      setOtpSent(false);
      setOtpVerified(false);
      setOtpToken('');
      setOtpCode('');
      setOtpError('');
    }
  };

  const handleFile = (file) => {
    if (!file) return;

    if (file.type !== 'application/pdf') {
      setError('Please upload a PDF file only.');
      return;
    }

    if (file.size > MAX_FILE_SIZE) {
      setError(`File size must be under 5MB. Your file is ${formatFileSize(file.size)}.`);
      return;
    }

    setResumeFile(file);
    setError('');
  };

  const removeFile = (e) => {
    e.stopPropagation();
    setResumeFile(null);
  };

  const handleRoleClick = (roleName) => {
    setForm((prev) => ({ ...prev, role: roleName }));
    document.getElementById('apply')?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!resumeFile) {
      setError('Please upload your resume.');
      return;
    }

    if (!otpVerified || otpVerifiedEmail !== form.email) {
      setError('Please verify your email with the code sent to it before submitting.');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const resumeBase64 = await fileToBase64(resumeFile);

      const res = await fetch('/api/careers', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...form,
          resumeBase64,
          resumeName: resumeFile.name,
          resumeType: resumeFile.type,
          otp: otpCode,
          otpToken,
        }),
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.error || 'Failed to submit application. Please try again.');
      }

      setSubmitted(true);
    } catch (err) {
      setError(err.message || 'Something went wrong. Please try again or email us directly.');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setForm({ name: '', education: '', role: '', email: '', phone: '', coverLetter: '' });
    setResumeFile(null);
    setSubmitted(false);
    setError('');
    setOtpToken('');
    setOtpCode('');
    setOtpSent(false);
    setOtpVerified(false);
    setOtpError('');
    setOtpVerifiedEmail('');
  };

  return (
    <main className="careers-page">
      <SEO
        title="Careers — Build the Future With ExploitEye"
        description="Join ExploitEye's growing team of engineers, designers, and strategists building secure, world-class digital products. Explore open positions and apply today."
        path="/careers"
        keywords="careers ExploitEye, software developer jobs India, tech jobs Chennai"
      />
      <section className="page-hero" style={{ backgroundImage: `url(${HERO_IMAGES.careers})` }}>
        <div className="container" style={{ position: 'relative', zIndex: 1 }}>
          <span className="section-label">
            <Briefcase size={11} /> Join the Team
          </span>
          <h1>
            Build the Future
            <br />
            <span className="gradient-text">With ExploitEye</span>
          </h1>
          <p>
            We're growing fast and looking for passionate engineers, designers, and strategists
            who want to build secure, world-class digital products.
          </p>
        </div>
      </section>

      <section style={{ padding: '80px 0', background: '#fff' }}>
        <div className="container">
          <div className="section-center" style={{ marginBottom: 48 }}>
            <span className="section-label">Perks</span>
            <h2 className="section-heading" style={{ textAlign: 'center', marginTop: 8 }}>
              Why Work Here?
            </h2>
          </div>
          <div className="perks-grid">
            {perks.map((p, i) => (
              <div key={i} className="perk-card">
                <div className="perk-icon">{p.icon}</div>
                <h4>{p.title}</h4>
                <p>{p.desc}</p>
              </div>
            ))}
          </div>

          <div className="section-center" style={{ marginBottom: 32 }}>
            <span className="section-label">Open Positions</span>
            <h2 className="section-heading" style={{ textAlign: 'center', marginTop: 8 }}>
              Current Openings
            </h2>
          </div>
          <div className="openings-list">
            {openings.map((o, i) => (
              <div key={i} className="opening-card">
                <div className="opening-info">
                  <div className="opening-role">{o.role}</div>
                  <div className="opening-meta">
                    <span className="opening-tag opening-tag--dept">{o.dept}</span>
                    <span className="opening-tag opening-tag--type">{o.type}</span>
                    {o.skills.map((s, j) => (
                      <span key={j} className="opening-tag opening-tag--skill">
                        {s}
                      </span>
                    ))}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => handleRoleClick(o.role)}
                  className="btn-ghost opening-apply-btn"
                >
                  Apply Now <ArrowRight size={14} />
                </button>
              </div>
            ))}
          </div>

          <div id="apply" className="apply-form-wrap">
            {submitted ? (
              <div className="apply-success">
                <CheckCircle2 size={56} />
                <h3>Application Received!</h3>
                <p>
                  Thank you for applying. Our team will review your profile and get back to you
                  within 5 business days. Please check your email for a confirmation.
                </p>
                <button type="button" className="btn-ghost" onClick={resetForm}>
                  Submit Another Application
                </button>
              </div>
            ) : (
              <>
                <h2>Apply Now</h2>
                <p>Fill in your details and we'll be in touch.</p>
                <form className="apply-form" onSubmit={handleSubmit}>
                  <div className="form-group">
                    <label htmlFor="name">Full Name *</label>
                    <input
                      id="name"
                      type="text"
                      name="name"
                      value={form.name}
                      onChange={handleChange}
                      placeholder="Your full name"
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="email">Email Address *</label>
                    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                      <input
                        id="email"
                        type="email"
                        name="email"
                        value={form.email}
                        onChange={handleChange}
                        placeholder="you@example.com"
                        required
                        style={{ flex: '1 1 260px', minWidth: 0 }}
                      />
                      <button
                        type="button"
                        className="btn-ghost"
                        onClick={handleSendOtp}
                        disabled={otpSending || !form.email}
                        style={{ whiteSpace: 'nowrap', flexShrink: 0 }}
                      >
                        {otpSending ? 'Sending...' : otpSent ? 'Resend Code' : 'Verify Email'}
                      </button>
                    </div>
                  </div>

                  {otpSent && !otpVerified && (
                    <div className="form-group">
                      <label htmlFor="otpCode">
                        Enter the 6-digit code sent to {form.email}
                      </label>
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <input
                          id="otpCode"
                          type="text"
                          inputMode="numeric"
                          maxLength={6}
                          value={otpCode}
                          onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, ''))}
                          placeholder="123456"
                          style={{ flex: 1, letterSpacing: '4px' }}
                        />
                        <button
                          type="button"
                          className="btn-primary"
                          onClick={handleVerifyOtp}
                          style={{ whiteSpace: 'nowrap' }}
                        >
                          Confirm Code
                        </button>
                      </div>
                    </div>
                  )}

                  {otpVerified && otpVerifiedEmail === form.email && (
                    <div className="form-group" style={{ color: 'var(--accent-emerald, #059669)', fontSize: '13px' }}>
                      <CheckCircle2 size={14} style={{ verticalAlign: 'middle', marginRight: '4px' }} />
                      Email verified
                    </div>
                  )}

                  {otpError && (
                    <div className="form-group--full">
                      <div className="form-error">
                        <AlertCircle size={16} />
                        <span>{otpError}</span>
                      </div>
                    </div>
                  )}

                  <div className="form-group">
                    <label htmlFor="phone">Phone Number</label>
                    <input
                      id="phone"
                      type="tel"
                      name="phone"
                      value={form.phone}
                      onChange={handleChange}
                      placeholder="+91 98765 43210"
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="education">Education *</label>
                    <input
                      id="education"
                      type="text"
                      name="education"
                      value={form.education}
                      onChange={handleChange}
                      placeholder="B.Tech Computer Science"
                      required
                    />
                  </div>

                  <div className="form-group form-group--full">
                    <label htmlFor="role">Applying For *</label>
                    <select
                      id="role"
                      name="role"
                      value={form.role}
                      onChange={handleChange}
                      required
                    >
                      <option value="">Select a role</option>
                      {openings.map((o, i) => (
                        <option key={i} value={o.role}>
                          {o.role}
                        </option>
                      ))}
                      <option value="Other / General Application">
                        Other / General Application
                      </option>
                    </select>
                  </div>

                  <div className="form-group form-group--full">
                    <label htmlFor="coverLetter">
                      Why ExploitEye? <span style={{ color: 'var(--text-3)', fontWeight: 400 }}>(optional)</span>
                    </label>
                    <textarea
                      id="coverLetter"
                      name="coverLetter"
                      value={form.coverLetter}
                      onChange={handleChange}
                      placeholder="Tell us why you'd be a great fit..."
                      rows={4}
                    />
                  </div>

                  <div className="form-group form-group--full">
                    <label>Resume (PDF, max 5MB) *</label>
                    {resumeFile ? (
                      <div className="file-attached">
                        <div className="file-attached__icon">
                          <FileText size={20} />
                        </div>
                        <div className="file-attached__info">
                          <strong>{resumeFile.name}</strong>
                          <span>{formatFileSize(resumeFile.size)}</span>
                        </div>
                        <button
                          type="button"
                          onClick={removeFile}
                          className="file-attached__remove"
                          aria-label="Remove file"
                        >
                          <X size={16} />
                        </button>
                      </div>
                    ) : (
                      <div
                        className={`file-drop ${dragOver ? 'file-drop--over' : ''}`}
                        onDragOver={(e) => {
                          e.preventDefault();
                          setDragOver(true);
                        }}
                        onDragLeave={() => setDragOver(false)}
                        onDrop={(e) => {
                          e.preventDefault();
                          setDragOver(false);
                          handleFile(e.dataTransfer.files[0]);
                        }}
                        onClick={() => document.getElementById('resume-input').click()}
                      >
                        <Upload size={22} style={{ margin: '0 auto 10px', color: 'var(--text-3)' }} />
                        <strong>Drop your PDF here</strong>
                        <p>or click to browse</p>
                        <input
                          id="resume-input"
                          type="file"
                          accept="application/pdf,.pdf"
                          style={{ display: 'none' }}
                          onChange={(e) => handleFile(e.target.files[0])}
                        />
                      </div>
                    )}
                  </div>

                  {error && (
                    <div className="form-group--full">
                      <div className="form-error">
                        <AlertCircle size={16} />
                        <span>{error}</span>
                      </div>
                    </div>
                  )}

                  <div className="form-group--full">
                    <button
                      type="submit"
                      className="btn-primary"
                      disabled={loading || !otpVerified || otpVerifiedEmail !== form.email}
                    >
                      {loading ? (
                        <>
                          <span className="spinner" /> Submitting...
                        </>
                      ) : (
                        <>
                          Submit Application <ArrowRight size={15} />
                        </>
                      )}
                    </button>
                  </div>
                </form>
              </>
            )}
          </div>
        </div>
      </section>

      <section className="cta-strip">
        <div className="container">
          <div className="cta-strip-inner">
            <div className="cta-strip-text">
              <h2>Don't See Your Role?</h2>
              <p>We're always open to talented people. Send us your portfolio.</p>
            </div>
            <div className="cta-strip-actions">
              <a href="mailto:careers@exploiteye.in" className="btn-primary">
                Send Portfolio <ArrowRight size={16} />
              </a>
              <Link to="/about" className="btn-ghost">
                About the Team
              </Link>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}